import { computed, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import { defineStore } from 'pinia';
import { useLoading } from '@sa/hooks';
import { SetupStoreId } from '@/enum';
import { useRouterPush } from '@/hooks/common/router';
import { fetchLogin } from '@/service/api';
import { disconnectStomp } from '@/service/ws/stomp-client';
import { localStg } from '@/utils/storage';
import { $t } from '@/locales';
import { invalidateAllRoles, invalidateAppStatPermissionOptions } from '@/hooks/business/system-manage-cache';
import { useRouteStore } from '../route';
import { clearAuthStorage, getToken, getUserInfo, normalizeUserInfo, refreshUserInfoFromServer } from './shared';

export const useAuthStore = defineStore(SetupStoreId.Auth, () => {
  const route = useRoute();
  const routeStore = useRouteStore();
  const { toLogin, redirectFromLogin } = useRouterPush(false);
  const { loading: loginLoading, startLoading, endLoading } = useLoading();

  const token = ref(getToken());

  const userInfo: Api.Auth.UserInfo = reactive(getUserInfo());

  /** is super role in static route */
  const isStaticSuper = computed(() => {
    const { VITE_AUTH_ROUTE_MODE, VITE_STATIC_SUPER_ROLE } = import.meta.env;

    return (
      VITE_AUTH_ROUTE_MODE === 'static' &&
      userInfo.roles !== undefined &&
      userInfo.roles.includes(VITE_STATIC_SUPER_ROLE)
    );
  });

  /** Is login */
  const isLogin = computed(() => Boolean(token.value));

  const isAdmin = computed(() => Boolean(userInfo.roles.includes('admin')));

  const isSubAdmin = computed(() => userInfo.roles.some(role => role === 'sub_admin' || role === 'subAdmin'));

  /** Reset auth store */
  async function resetStore() {
    const authStore = useAuthStore();

    invalidateAllRoles();
    invalidateAppStatPermissionOptions();

    clearAuthStorage();

    disconnectStomp();

    authStore.$reset();

    if (!route.meta.constant) {
      await toLogin();
    }

    routeStore.resetStore();
  }

  /**
   * Login
   *
   * @param userName User name
   * @param password Password
   * @param [redirect=true] Whether to redirect after login. Default is `true`
   */
  async function login(userName: string, password: string, redirect = true) {
    startLoading();

    const { data: loginToken, error } = await fetchLogin(userName, password);

    if (!error) {
      const pass = await loginByToken(loginToken);

      if (pass) {
        await routeStore.initAuthRoute();

        if (redirect) {
          await redirectFromLogin();
        }

        if (routeStore.isInitAuthRoute) {
          window.$notification?.success({
            title: $t('page.login.common.loginSuccess'),
            content: $t('page.login.common.welcomeBack', { userName: userInfo.userName }),
            duration: 4500
          });
        }
      }
    } else {
      resetStore();
    }

    endLoading();
  }

  async function refreshUserInfo() {
    const normalized = await refreshUserInfoFromServer();

    if (normalized) {
      token.value = getToken();
      Object.assign(userInfo, normalized);
      return true;
    }

    if (!getToken()) {
      token.value = '';
      Object.assign(userInfo, normalizeUserInfo({}));
    }

    return false;
  }

  async function loginByToken(loginToken: Api.Auth.LoginToken) {
    localStg.set('token', loginToken.token);
    // localStg.set('refreshToken', loginToken.refreshToken);
    token.value = loginToken.token;
    const pass = await refreshUserInfo();
    return pass;
  }

  function updateUserStatPermsLocal(payload: {
    appStatPerms: Api.Application.App[];
    channelStatPerms: string[];
    financeStatPerm: number;
  }) {
    userInfo.appStatPerms = payload.appStatPerms;
    userInfo.channelStatPerms = payload.channelStatPerms;
    userInfo.financeStatPerm = payload.financeStatPerm;
    localStg.set('userInfo', normalizeUserInfo(userInfo));
  }

  return {
    token,
    userInfo,
    isStaticSuper,
    isLogin,
    isAdmin,
    isSubAdmin,
    loginLoading,
    resetStore,
    login,
    refreshUserInfo,
    updateUserStatPermsLocal
  };
});
